// Phase Q.1 — Connection state surfacing.
//
// Derives a single user-facing connection state from two underlying signals:
//   1. navigator.onLine — coarse "browser thinks the network is up"
//   2. RealtimeConnection status — the SignalR hub state
//
// The PWA renders a small badge in the app-shell that reflects this derived
// state. Doesn't change request-handling behavior (Q.2 + Q.3 build that
// on top). Foundation only.

import { HubConnectionState } from '@microsoft/signalr';
import type {
  RealtimeConnection,
  RealtimeConnectionStatus,
} from '../realtime/realtime-connection';

export type ConnectionState = 'online' | 'reconnecting' | 'offline';

export interface ConnectionStateSnapshot {
  state: ConnectionState;
  // True iff the underlying SignalR layer has been told it's disabled
  // (server returned 503 — no AzureSignalRConnectionString in this
  // deployment). In disabled mode we always render 'online' (the app
  // can still call HTTP endpoints; realtime is just not a thing here).
  realtimeDisabled: boolean;
  // N.7a — server returned 403 from negotiate because the shop's tier
  // doesn't unlock realtime. HTTP path works fine; polling fallback
  // takes over. Surfaced as a friendly amber banner.
  tierBlocked?: {
    currentTier: string;
    requiredTier: string;
    message: string;
  };
  // Last time the state changed, for UX hints ("offline for 2 min").
  // Resets on every transition.
  sinceUtc: string;
}

export type ConnectionStateListener = (
  snapshot: ConnectionStateSnapshot
) => void;

// Tiny observable store. One instance per app load.
export class ConnectionStateStore {
  private _snapshot: ConnectionStateSnapshot = {
    state: 'online',
    realtimeDisabled: false,
    sinceUtc: new Date().toISOString(),
  };
  private _listeners = new Set<ConnectionStateListener>();
  // Tracked separately so derive() can fold both signals together.
  private _navigatorOnline =
    typeof navigator !== 'undefined' ? navigator.onLine : true;
  private _realtimeStatus: RealtimeConnectionStatus | null = null;
  private _realtimeUnsub?: () => void;

  // Wire this store to a RealtimeConnection. Safe to call multiple times —
  // an existing wiring is detached first.
  attachRealtime(realtime: RealtimeConnection): void {
    this._realtimeUnsub?.();
    this._realtimeUnsub = realtime.onStatusChange((status) => {
      this._realtimeStatus = status;
      this._recompute();
    });
  }

  // Wire navigator.online events. Safe to call once at app start.
  attachNavigator(target: Window = window): () => void {
    const onOnline = () => {
      this._navigatorOnline = true;
      this._recompute();
    };
    const onOffline = () => {
      this._navigatorOnline = false;
      this._recompute();
    };
    target.addEventListener('online', onOnline);
    target.addEventListener('offline', onOffline);
    return () => {
      target.removeEventListener('online', onOnline);
      target.removeEventListener('offline', onOffline);
    };
  }

  get snapshot(): ConnectionStateSnapshot {
    return this._snapshot;
  }

  subscribe(listener: ConnectionStateListener): () => void {
    this._listeners.add(listener);
    listener(this._snapshot);
    return () => this._listeners.delete(listener);
  }

  // Pure derivation — no side effects beyond setting _snapshot + firing
  // listeners. Mapping:
  //   - navigator offline → offline (browser's authoritative on this)
  //   - realtime disabled (503) → online (HTTP still works; no realtime
  //     channel to be "offline" on)
  //   - tier-blocked (403) → online (HTTP still works; polling fallback
  //     handles propagation; banner surfaces the upgrade prompt)
  //   - SignalR Connected → online
  //   - SignalR Reconnecting/Connecting → reconnecting
  //   - Anything else (Disconnected, no status yet, transient) →
  //     offline. The next HTTP call will confirm or correct.
  private _recompute(): void {
    const next = this._derive();
    const prev = this._snapshot;
    const tierBlockedChanged =
      JSON.stringify(prev.tierBlocked ?? null) !==
      JSON.stringify(next.tierBlocked ?? null);
    if (
      prev.state === next.state &&
      prev.realtimeDisabled === next.realtimeDisabled &&
      !tierBlockedChanged
    ) {
      return;
    }
    this._snapshot = { ...next, sinceUtc: new Date().toISOString() };
    for (const listener of this._listeners) listener(this._snapshot);
  }

  private _derive(): Omit<ConnectionStateSnapshot, 'sinceUtc'> {
    const realtimeDisabled = this._realtimeStatus?.disabled ?? false;
    const tierBlocked = this._realtimeStatus?.tierBlocked;
    if (!this._navigatorOnline) {
      return { state: 'offline', realtimeDisabled, tierBlocked };
    }
    if (realtimeDisabled) {
      // Realtime turned off at the server; HTTP path is the only channel
      // and we have no signal on it from here. Trust navigator.online.
      return { state: 'online', realtimeDisabled, tierBlocked };
    }
    if (tierBlocked) {
      // Tier doesn't unlock realtime; HTTP path works, polling fallback
      // takes over. Render as 'online' so the connection chip stays
      // green; the dedicated tier banner does the upsell.
      return { state: 'online', realtimeDisabled, tierBlocked };
    }
    const hubState =
      this._realtimeStatus?.state ?? HubConnectionState.Disconnected;
    switch (hubState) {
      case HubConnectionState.Connected:
        return { state: 'online', realtimeDisabled, tierBlocked };
      case HubConnectionState.Connecting:
      case HubConnectionState.Reconnecting:
        return { state: 'reconnecting', realtimeDisabled, tierBlocked };
      default:
        // Disconnected / Disconnecting / unknown — treat as offline so
        // the badge flags the user-facing problem. If the user is mid-
        // auth bootstrap (no SignalR yet) the state will flip to online
        // on first successful negotiate.
        return { state: 'offline', realtimeDisabled, tierBlocked };
    }
  }
}

// Single shared store, attached at app start. Components subscribe via
// connectionStateStore.subscribe(...).
export const connectionStateStore = new ConnectionStateStore();
